import {
  PrimaryButton,
  Stack,
  TextField,
  useTheme,
  Checkbox,
  Icon,
  Spinner
} from "@fluentui/react";
import { useEffect, useRef, useState, useMemo } from "react";
import {
  callingWidgetSetupContainerStyles,
  checkboxStyles,
  startCallButtonStyles,
  callingWidgetContainerStyles,
  callIconStyles,
  logoContainerStyles,
  callingWidgetInCallContainerStyles
} from "../styles/CallingWidgetComponent.styles";
import {
  AzureCommunicationTokenCredential,
  CommunicationUserIdentifier,
  MicrosoftTeamsAppIdentifier,
} from "@azure/communication-common";
import {
  CallAdapter,
  CallAdapterState,
  CallComposite,
  CommonCallAdapterOptions,
  StartCallIdentifier,
  createAzureCommunicationCallAdapter,
} from "@azure/communication-react";
import { isMobile } from "react-device-detect";

export type WidgetAdapterArgs = {
  token: string;
  userId: CommunicationUserIdentifier;
  teamsAppIdentifier: MicrosoftTeamsAppIdentifier;
  userDisplayName: string;
};

export interface CallingWidgetComponentProps {
  widgetAdapterArgs: WidgetAdapterArgs;
  onRenderLogo?: () => JSX.Element;
}

export const CallingWidgetComponent = (
  props: CallingWidgetComponentProps
): JSX.Element => {
  const { onRenderLogo, widgetAdapterArgs } = props;
  const theme = useTheme();

  // State variables
  const [widgetState, setWidgetState] = useState<"new" | "setup" | "inCall">("setup");
  const [displayName, setDisplayName] = useState<string>(widgetAdapterArgs.userDisplayName || "");
  const [useLocalVideo, setUseLocalVideo] = useState<boolean>(false);
  const [adapter, setAdapter] = useState<CallAdapter>();
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const callIdRef = useRef<string>();

  // Memoized values for performance optimization
  const credential = useMemo(() => {
    try {
      return new AzureCommunicationTokenCredential(widgetAdapterArgs.token);
    } catch (error) {
      console.error("Failed to construct token credential:", error);
      return undefined;
    }
  }, [widgetAdapterArgs.token]);

  const adapterOptions: CommonCallAdapterOptions = useMemo(() => ({
    callingSounds: {
      callEnded: { url: "/sounds/callEnded.mp3" },
      callRinging: { url: "/sounds/callRinging.mp3" },
      callBusy: { url: "/sounds/callBusy.mp3" },
    },
  }), []);

  const callAdapterArgs = useMemo(() => ({
    userId: widgetAdapterArgs.userId,
    credential: credential,
    targetCallees: [widgetAdapterArgs.teamsAppIdentifier] as StartCallIdentifier[],
    displayName: displayName,
    options: adapterOptions,
  }), [
    widgetAdapterArgs.userId,
    widgetAdapterArgs.teamsAppIdentifier,
    credential,
    displayName,
    adapterOptions
  ]);

  // Media permission request
  const requestMediaPermissions = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      mediaStreamRef.current = stream;
      return true;
    } catch (error) {
      console.error("Error requesting media permissions:", error);
      alert("Could not access media devices. Please ensure you have granted the necessary permissions.");
      return false;
    }
  };

  // Cleanup media stream
  const stopMediaStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  // Handle adapter creation and state changes
  useEffect(() => {
    const initializeAdapter = async () => {
      if (useLocalVideo && callAdapterArgs.credential && !adapter) {
        const newAdapter = await createAzureCommunicationCallAdapter({
          displayName: callAdapterArgs.displayName ?? "Customer",
          userId: callAdapterArgs.userId,
          credential: callAdapterArgs.credential,
          targetCallees: callAdapterArgs.targetCallees,
          options: callAdapterArgs.options,
        });
        setAdapter(newAdapter);
      }
    };

    initializeAdapter();

    return () => {
      stopMediaStream();
      if (adapter) {
        adapter.dispose();
      }
    };
  }, [useLocalVideo, callAdapterArgs, adapter]);

  useEffect(() => {
    if (adapter) {
      const handleCallEnd: any = () => {
        if (
          adapter.getState().acceptedTransferCallState &&
          adapter.getState().acceptedTransferCallState?.id !== callIdRef.current
        ) {
          return;
        }
        resetWidgetState();
      };

      adapter.on("callEnded", handleCallEnd);
      adapter.onStateChange((state: CallAdapterState) => {
        if (state?.call?.id && callIdRef.current !== state?.call?.id) {
          callIdRef.current = state?.call?.id;
          console.log(`Call Id: ${callIdRef.current}`);
        }
      });

      return () => {
        adapter.off("callEnded", handleCallEnd);
      };
    }
  }, [adapter]);

  const resetWidgetState = () => {
    setDisplayName("");
    setUseLocalVideo(false);
    setWidgetState("setup");
    setAdapter(undefined);
  };

  // Widget render logic
  if (widgetState === "setup") {
    return (
      <Stack styles={callingWidgetSetupContainerStyles(theme)} tokens={{ childrenGap: "1rem" }}>
        <Stack tokens={{ childrenGap: "1rem" }} styles={logoContainerStyles}>
          <Stack style={{ transform: "scale(1.8)" }}>
            {onRenderLogo && onRenderLogo()}
          </Stack>
        </Stack>
        <h2 className="flex text-lg text-pretty font-sans font-bold" style={{ marginBottom: '-1.4rem' }}>
          Enter your name and accept terms to continue
        </h2>  
        <TextField
          label={"Name (required)"}
          defaultValue={displayName}
          placeholder={"Enter your name"}
          onChange={(_, newValue) => setDisplayName(newValue || "")}
        />
        <Checkbox
          required={true}
          styles={checkboxStyles(theme)}
          disabled={!displayName}
          checked={useLocalVideo}
          label={"Use video - Checking this box will enable camera controls and screen sharing"}
          onChange={async (_, checked?: boolean) => {
            if (checked && await requestMediaPermissions()) {
              setUseLocalVideo(true);
            } else {
              setUseLocalVideo(false);
              stopMediaStream();
              setAdapter(undefined);
            }
          }}
        />
        {adapter ? (
          <PrimaryButton
            styles={startCallButtonStyles(theme)}
            onClick={() => {
              setWidgetState("inCall");
              adapter.startCall(callAdapterArgs.targetCallees, { audioOptions: { muted: false } });
            }}
          >
            Start Call
          </PrimaryButton>
        ) : (
          <Spinner ariaLive="assertive" labelPosition="top" />
        )}
      </Stack>
    );
  }

  if (widgetState === "inCall" && adapter) {
    return (
      <Stack styles={callingWidgetInCallContainerStyles(theme)}>
        <CallComposite
          adapter={adapter}
          formFactor= "desktop" //{isMobile ? "mobile" : "desktop"}
          options={{
            callControls: {
              cameraButton: useLocalVideo,
              screenShareButton: useLocalVideo,
              moreButton: true,
              peopleButton: true,
              dtmfDialerButton: false,
              displayType: "compact",
            },
            disableAutoShowDtmfDialer: true,
            localVideoTile: useLocalVideo ? { position: "floating" } : false,
          }}
        />
      </Stack>
    );
  }

  return (
    <Stack
      horizontalAlign="center"
      verticalAlign="center"
      styles={callingWidgetContainerStyles(theme)}
      onClick={() => setWidgetState("setup")}
    >
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        style={{
          height: "4rem",
          width: "4rem",
          borderRadius: "50%",
          background: theme.palette.themePrimary,
        }}
      >
        <Icon iconName="callAdd" styles={callIconStyles(theme)} />
      </Stack>
    </Stack>
  );
};
